/**
 * まなびモードの**バランス定数の単一の置き場**(CLAUDE.md ルール3)。
 *
 * ここ以外の場所に数値を直書きしないこと。星のしきい値・1単元の問題数・
 * 出題の既定挙動を変えるときはこのファイルだけを触る。
 * `tools/content-build` もここを読んで**ビルド時に**データを検査する
 * (問題数が足りない単元を配信してしまわないように)。
 */

/** 選択肢の数の範囲(小さい子が一度に見比べられる数)。 */
export const QUIZ_CHOICES = {
  /** 最少。○×形式の2択。 */
  min: 2,
  /** 最多。4つより多いと未就学児には選び切れない。 */
  max: 4,
} as const;

/** 1単元の構成。 */
export const QUIZ_UNIT = {
  /** 1単元の問題数の下限(これを下回る単元はビルドエラー)。 */
  minQuestions: 8,
  /**
   * 1単元の問題数の上限(超えるなら単元を分ける)。
   *
   * 手書き単元は10問前後だが、テンプレート生成の単元は**1単元100問級**になる
   * (docs/13 §4。プールが大きいほど反復しても新鮮さが残る)。
   * 1回のプレイは `sessionQuestions` 問だけを抜き出すので、
   * ここが大きくても子どもが見る画面の長さは変わらない。
   * 上限そのものは「生成器の暴走で1万問を配信してしまう」事故を止めるためのもの。
   */
  maxQuestions: 200,
  /** 1回のプレイで出す問題数。単元の問題数がこれ未満なら全問出す。 */
  sessionQuestions: 10,
} as const;

/**
 * 単元内ステージ(公文式ドリル。docs/13 §11)の区切りかた。
 *
 * ステージ = 単元の問題プールを**現行の並び順のまま**この問数ずつ切ったもの。
 * ビルド時に確定して配信JSONに載るので、プールが増えればステージも自動で増える
 * (100問 = 10ステージ)。並びを動かさないのは、進捗・にがて記録・音声が
 * 問題IDに紐づいていて、**同じステージを再挑戦しても同じ並び**(公文のプリント)に
 * したいため。
 */
export const QUIZ_STAGE = {
  /**
   * 1ステージの目安の問数。ステージ数は `ceil(単元の問題数 / この値)` で決まる。
   *
   * 「10問ずつ切って端数を最後に寄せる」ではなく**均等割り**にしてある(確定 2026-08-22)。
   * 端数を寄せると16〜17問のステージができて、小学生には1回が長すぎたため。
   */
  questions: 10,
  /**
   * この問数までの単元は**1ステージのまま**にする。
   *
   * 13問を 7+6 に割るのは意味があるが、12問を 6+6 に割ると
   * 「1ステージ = ひとまとまりの練習」という感覚が薄れる。
   */
  singleStageMax: 12,
  /**
   * 1ステージの問数の下限(これを下回るステージはビルドエラー)。
   *
   * 均等割りなので、13問以上の単元では必ず6問以上になる。下限が効くのは
   * 「単元の問題そのものが少ない」ときだけ(`QUIZ_UNIT.minQuestions` = 8 が先に効く)。
   */
  minQuestions: 5,
} as const;

/**
 * 単元スコアの星(3段階)。正答率のしきい値。
 *
 * `full` = 全問正解 / `good` = 8割 / `clear` = クリア。
 * これを下回ると星0(「もういちど」)。**星0でも必ずほめる**のは UI の仕事。
 */
export const QUIZ_STARS = {
  full: 1,
  good: 0.8,
  clear: 0.6,
} as const;

/**
 * ふくしゅう(まちがい直し)の決めごと(公文式の反復学習の土台)。
 *
 * 問題ごとの解答履歴から「にがてな もんだい」を選び、優先度の高い順に出し直す。
 * しきい値をここに集めておくのは、あとで「2回連続で正解しないと消えない」のような
 * 締め方に変えるときに**このファイルだけ**を触れば済むようにするため。
 */
export const QUIZ_REVIEW = {
  /** 1回のふくしゅうで出す問題数の上限(単元プレイと同じ長さに揃える)。 */
  sessionQuestions: 10,
  /**
   * 「累計で誤答が多い」とみなす誤答回数。
   * これ以上まちがえた問題は、1回正解しただけでは苦手プールから抜けない。
   */
  repeatedWrong: 3,
  /** ふつうの苦手を解消するのに要る連続正解の回数。 */
  clearStreak: 1,
  /** 何度もまちがえた問題(`repeatedWrong` 以上)を解消するのに要る連続正解の回数。 */
  repeatedClearStreak: 2,
  /**
   * 記録しておく問題数の上限。
   *
   * 問題は今後数千問に増えるので、**全問ぶんは持たない**。上限を超えたら
   * 「苦手 → 直近といた順」で残す(苦手は消さず、解けている古い問題から捨てる)。
   * 1問あたり約45バイトなので、300問でも 15KB 程度に収まる。
   */
  maxTracked: 300,
} as const;

/**
 * 単元冒頭の解説パート(docs/13 §8)の決めごと。
 *
 * `minFrames` / `maxFrames` は「壊れたシナリオを配信しない」ための安全弁で、
 * 台本を書くときの設計値は `designMinFrames` 〜 `designMaxFrames` のほう。
 * 盤面の座標系は**この幅・高さの中の位置**として書く(描画側の px には依存しない)。
 */
export const QUIZ_LESSON = {
  /** コマ数の下限(1コマだけの「解説」は解説になっていない)。 */
  minFrames: 2,
  /** コマ数の上限。これを超えるなら単元を分けるか、解説を2本に割る。 */
  maxFrames: 40,
  /**
   * 台本の設計値の下限。これを下回る解説は「考え方」まで届いていない
   * (ポカのまちがい → パルのとまどい → 種明かし → まとめ、だけで10コマは要る)。
   */
  designMinFrames: 10,
  /**
   * 台本の設計値の上限(ユーザー決定 2026-08-22。旧15コマ)。
   *
   * 15コマに収めるために手順を1コマへ詰め込むより、**分かりやすさを優先**して
   * コマを割ってよい。目安は10〜18コマで、手順の多い単元だけがここまで伸びる。
   * これを超えるなら単元そのものを分ける合図。
   */
  designMaxFrames: 24,
  /** 1コマのセリフの長さの上限(吹き出しに1度に収まる量)。 */
  maxTextLength: 60,
  /** 1コマに書ける盤面アクションの数。 */
  maxActions: 6,
  /** 盤面のスロット数の上限。 */
  maxSlots: 20,
  /** 盤面の座標系(左上が原点。スロットの x / y はこの中の**中心**位置)。 */
  boardWidth: 100,
  boardHeight: 60,
  /** スロットの既定の大きさ(盤面座標)。 */
  slotWidth: 14,
  slotHeight: 16,
} as const;

/** 出題の既定挙動。 */
export const QUIZ_SESSION = {
  /** 問題の並びをシャッフルする(同じシードなら必ず同じ並び)。 */
  shuffleQuestions: true,
  /** 選択肢の並びをシャッフルする(「いつも2番目が正解」を防ぐ)。 */
  shuffleChoices: true,
} as const;
